/**
 * Conversión entre medidas crudas del DOM y `rem`.
 *
 * Regla del proyecto: **toda longitud que llega a un estilo va en `rem`**.
 * Este módulo es el ÚNICO lugar autorizado a leer píxeles, y lo hace para
 * devolverlos ya convertidos. Nada de dispersar `* 16` o `/ 16` por los
 * componentes: si hace falta convertir, se llama a algo de aquí.
 *
 * El root se queda en el tamaño por defecto del navegador (16 píxeles salvo que el
 * usuario cambie el zoom o la preferencia de tipografía), así que las
 * conversiones se hacen contra el tamaño real medido, no contra una constante.
 */

const FALLBACK_ROOT_PX = 16;

let cachedRootPx: number | null = null;

/** Tamaño de fuente efectivo del elemento raíz, en píxeles. */
export function rootFontSizePx(): number {
  if (cachedRootPx !== null) return cachedRootPx;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return FALLBACK_ROOT_PX;
  }
  const raw = getComputedStyle(document.documentElement).fontSize;
  const parsed = Number.parseFloat(raw);
  cachedRootPx = Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK_ROOT_PX;
  return cachedRootPx;
}

/** Olvida la medida cacheada (tests, o cambios de zoom del navegador). */
export function resetRootFontSizeCache(): void {
  cachedRootPx = null;
}

/**
 * Convierte una medida del DOM en una longitud `rem` lista para usar como
 * valor CSS. La única entrada aceptada es una medida cruda: `scrollHeight`,
 * `offsetTop`, `getBoundingClientRect().width`, coordenadas de puntero.
 */
export function pxToRem(px: number): string {
  return `${pxToRemNumber(px)}rem`;
}

/** Igual que `pxToRem` pero devuelve el número, para operar antes de formatear. */
export function pxToRemNumber(px: number): number {
  const unit = rootFontSizePx();
  const value = px / unit;
  // 4 decimales es más que suficiente (1/10000 rem ≈ 0.0016 píxeles) y evita
  // arrastrar basura de coma flotante a los estilos.
  return Math.round(value * 10_000) / 10_000;
}

/** Cualquier medida del DOM (alto, ancho, offset, coordenada) → `rem`. */
export function domLengthToRem(value: number): number {
  return pxToRemNumber(value);
}

/** Lee una propiedad numérica de layout de un elemento y la devuelve en `rem`. */
export function elementSizeToRem(
  element: Element | null,
  property: "offsetWidth" | "offsetHeight" | "clientWidth" | "clientHeight" | "scrollWidth" | "scrollHeight",
): number {
  if (!(element instanceof HTMLElement)) return 0;
  const value = element[property];
  return typeof value === "number" ? pxToRemNumber(value) : 0;
}

/**
 * Los gráficos SVG no hablan `rem`: recharts y todo SVG dibujan en un espacio
 * de coordenadas sin unidades. Esta es la frontera de vuelta — se pasa la
 * longitud en `rem` (que es como se piensa el diseño) y devuelve el número de
 * unidades que ese mismo grosor ocupa en el viewBox 1:1 con el layout.
 */
export function remToSvgUnits(rem: number): number {
  return rem * rootFontSizePx();
}
